// src/services/session-notes-service.js
// Reads and writes the sessionNotes field on booking documents.

import { db } from '../config/firebase-config';
import {
  doc, getDoc, updateDoc, deleteField,
  collection, query, where, orderBy, getDocs,
  serverTimestamp,
} from 'firebase/firestore';

/**
 * Save (create or update) session notes on a booking document.
 * Preserves the original createdAt if notes already exist.
 */
export const saveSessionNotes = async (bookingId, { content, plainText, themes, sharedWithClient }) => {
  if (!db || !bookingId) throw new Error('Missing required args');
  const ref = doc(db, 'bookings', bookingId);
  const snap = await getDoc(ref);
  const existing = snap.data()?.sessionNotes;
  await updateDoc(ref, {
    sessionNotes: {
      content,
      plainText: plainText || '',
      themes: themes || [],
      sharedWithClient: sharedWithClient ?? existing?.sharedWithClient ?? false,
      sharedAt: sharedWithClient ? (existing?.sharedAt || new Date().toISOString()) : (existing?.sharedAt || null),
      updatedAt: serverTimestamp(),
      createdAt: existing?.createdAt || serverTimestamp(),
    },
  });
};

/**
 * Remove session notes from a booking document.
 */
export const deleteSessionNotes = async (bookingId) => {
  if (!db || !bookingId) return;
  await updateDoc(doc(db, 'bookings', bookingId), {
    sessionNotes: deleteField(),
  });
};

/**
 * Return all bookings for a client that have session notes, sorted newest first.
 */
export const getNotesForClient = async (practitionerId, clientEmail) => {
  if (!db || !practitionerId || !clientEmail) return [];
  try {
    const q = query(
      collection(db, 'bookings'),
      where('practitionerId', '==', practitionerId),
      where('client.email', '==', clientEmail),
      orderBy('dateTime', 'desc'),
    );
    const snap = await getDocs(q);
    const results = [];
    snap.forEach(d => {
      const data = d.data();
      if (data.sessionNotes) results.push({ id: d.id, ...data });
    });
    return results;
  } catch (err) {
    console.error('getNotesForClient error:', err);
    return [];
  }
};
